import { useState } from "react";
import { Send, CheckCircle, UserSearch, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formText = {
  it: {
    title: "Esercita i tuoi diritti GDPR",
    subtitle: "Compila il modulo per inviare una richiesta di accesso o cancellazione dei tuoi dati personali.",
    typeLabel: "Tipo di richiesta",
    typeAccess: "Accesso ai dati",
    typeDeletion: "Cancellazione dei dati",
    nameLabel: "Nome e Cognome",
    namePlaceholder: "Inserisci il tuo nome completo",
    emailLabel: "Indirizzo Email",
    emailPlaceholder: "nome@esempio.it",
    messageLabel: "Messaggio (opzionale)",
    messagePlaceholder: "Fornisci ulteriori dettagli sulla tua richiesta...",
    submit: "Invia Richiesta",
    sending: "Invio in corso...",
    successTitle: "Richiesta Inviata",
    successMsg: "La tua richiesta e stata registrata con successo. Ti risponderemo entro 30 giorni come previsto dal GDPR.",
    errorMsg: "Si e verificato un errore. Riprova o contattaci a info@sisaline.it",
    newRequest: "Nuova richiesta",
    required: "Campo obbligatorio",
  },
  en: {
    title: "Exercise Your GDPR Rights",
    subtitle: "Fill out the form to submit a data access or deletion request for your personal data.",
    typeLabel: "Request Type",
    typeAccess: "Data Access",
    typeDeletion: "Data Deletion",
    nameLabel: "Full Name",
    namePlaceholder: "Enter your full name",
    emailLabel: "Email Address",
    emailPlaceholder: "name@example.com",
    messageLabel: "Message (optional)",
    messagePlaceholder: "Provide additional details about your request...",
    submit: "Submit Request",
    sending: "Sending...",
    successTitle: "Request Submitted",
    successMsg: "Your request has been successfully registered. We will respond within 30 days as required by the GDPR.",
    errorMsg: "An error occurred. Please try again or contact us at info@sisaline.it",
    newRequest: "New request",
    required: "Required field",
  },
};

export default function GdprRequestForm({ lang = "it" }) {
  const [requestType, setRequestType] = useState("access");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const t = formText[lang] || formText.it;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/gdpr-requests`, {
        request_type: requestType,
        full_name: fullName,
        email,
        message,
      });
      setSuccess(true);
    } catch (err) {
      setError(t.errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSuccess(false);
    setFullName("");
    setEmail("");
    setMessage("");
    setRequestType("access");
    setError("");
  };

  if (success) {
    return (
      <div
        data-testid="gdpr-form-success"
        className="bg-white rounded-2xl border border-slate-200 p-8 text-center"
      >
        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-[#111111] mb-2">{t.successTitle}</h3>
        <p className="text-base text-slate-600 leading-relaxed mb-6">{t.successMsg}</p>
        <Button
          data-testid="gdpr-form-new-request"
          variant="outline"
          onClick={resetForm}
          className="rounded-full"
        >
          {t.newRequest}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="gdpr-request-form">
      <h3 className="text-xl sm:text-2xl font-bold text-[#111111] mb-2">{t.title}</h3>
      <p className="text-sm text-slate-500 mb-6">{t.subtitle}</p>
      <Separator className="mb-6" />

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Request Type */}
        <div>
          <label className="block text-sm font-semibold text-[#111111] mb-3">{t.typeLabel}</label>
          <div className="flex gap-3">
            <button
              type="button"
              data-testid="gdpr-type-access"
              onClick={() => setRequestType("access")}
              className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                requestType === "access"
                  ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]"
                  : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              <UserSearch className="w-4 h-4" />
              {t.typeAccess}
            </button>
            <button
              type="button"
              data-testid="gdpr-type-deletion"
              onClick={() => setRequestType("deletion")}
              className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                requestType === "deletion"
                  ? "border-red-500 bg-red-50 text-red-600"
                  : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              <Trash2 className="w-4 h-4" />
              {t.typeDeletion}
            </button>
          </div>
        </div>

        {/* Full Name */}
        <div>
          <label className="block text-sm font-semibold text-[#111111] mb-1.5">{t.nameLabel} *</label>
          <input
            data-testid="gdpr-input-name"
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={t.namePlaceholder}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-[#111111] mb-1.5">{t.emailLabel} *</label>
          <input
            data-testid="gdpr-input-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.emailPlaceholder}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all"
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-semibold text-[#111111] mb-1.5">{t.messageLabel}</label>
          <textarea
            data-testid="gdpr-input-message"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t.messagePlaceholder}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all resize-none"
          />
        </div>

        {error && (
          <p data-testid="gdpr-form-error" className="text-sm text-red-600">{error}</p>
        )}

        <Button
          data-testid="gdpr-submit-button"
          type="submit"
          disabled={loading}
          className="w-full bg-[#002FA7] text-white hover:bg-[#001f75] rounded-full py-2.5 font-semibold transition-all"
        >
          {loading ? (
            t.sending
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Send className="w-4 h-4" />
              {t.submit}
            </span>
          )}
        </Button>
      </form>
    </div>
  );
}
